import React, { useMemo } from 'react';
import { ShieldCheckIcon, ClockIcon, UserGroupIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import type { WhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';
import { isApprovedWhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';
import type { CampaignFormState } from './whatsappCampaignFormUtils';
import { emptyRetryHeaderMediaSlot, inputClass } from './whatsappCampaignFormUtils';
import WhatsAppTemplateSearchSelect from './WhatsAppTemplateSearchSelect';
import WhatsAppMediaHeaderUploader from './WhatsAppMediaHeaderUploader';
import { isMediaHeaderFormat } from './whatsappPreview';

type Props = {
  form: CampaignFormState;
  setForm: React.Dispatch<React.SetStateAction<CampaignFormState>>;
  audienceCount: number;
  templates: WhatsAppTemplate[];
  marketingOnly?: boolean;
};

function buildPhasePreview(count: number, phaseSize: number, sendWindowHours: number) {
  const size = Math.min(250, Math.max(1, phaseSize || 250));
  const phases = Math.max(1, Math.ceil(Math.max(0, count) / size));
  const intervalHours = phases <= 1 ? 0 : sendWindowHours / phases;
  return { phases, intervalHours, phaseSize: size };
}

const WhatsAppCampaignPolicySection: React.FC<Props> = ({
  form,
  setForm,
  audienceCount,
  templates,
  marketingOnly = true,
}) => {
  const isMarketing = form.messageCategory === 'MARKETING';
  const show = !marketingOnly || isMarketing;
  const preview = useMemo(
    () =>
      buildPhasePreview(
        audienceCount,
        form.sendPolicy.phaseSize || 250,
        form.sendPolicy.sendWindowHours || 24
      ),
    [audienceCount, form.sendPolicy.phaseSize, form.sendPolicy.sendWindowHours]
  );

  const approvedTemplates = templates.filter((t) => isApprovedWhatsAppTemplate(t));

  if (!show) return null;

  const updatePolicy = (patch: Partial<CampaignFormState['sendPolicy']>) => {
    setForm((prev) => ({ ...prev, sendPolicy: { ...prev.sendPolicy, ...patch } }));
  };

  const updateRetryTemplate = (index: number, templateName: string) => {
    setForm((prev) => {
      const plan = [...(prev.retryTemplatePlan || [])];
      while (plan.length < 7) plan.push(prev.templateName);
      plan[index] = templateName;
      return { ...prev, retryTemplatePlan: plan };
    });
  };

  const updateRetryHeader = (
    index: number,
    media: { headerMediaUrl: string | null; headerMimeType: string | null; headerFileName: string | null }
  ) => {
    setForm((prev) => {
      const plan = [...(prev.retryHeaderMediaPlan || [])];
      while (plan.length < 7) plan.push(emptyRetryHeaderMediaSlot());
      plan[index] = {
        headerMediaUrl: media.headerMediaUrl || '',
        headerMimeType: media.headerMimeType || '',
        headerFileName: media.headerFileName || '',
      };
      return { ...prev, retryHeaderMediaPlan: plan, applyRetryHeaderToAll: false };
    });
  };

  const campaignTemplate = templates.find((t) => t.name === form.templateName);
  const campaignNeedsMedia = isMediaHeaderFormat(campaignTemplate?.headerFormat);
  const campaignHasMedia = Boolean(form.headerMediaUrl.trim());

  return (
    <section className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50/80 to-white p-5 dark:border-indigo-900/40 dark:from-indigo-950/30 dark:to-gray-900">
      <div className="mb-4 flex items-start gap-3">
        <div className="rounded-xl bg-indigo-100 p-2 dark:bg-indigo-900/40">
          <ShieldCheckIcon className="h-5 w-5 text-indigo-700 dark:text-indigo-300" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Marketing send policy</h3>
          <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
            Meta-safe pacing, weekly cap, non-reader exclusion, and 7-day retry templates.
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-white/80 bg-white/90 p-4 dark:border-gray-700 dark:bg-gray-900/60">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
            <ClockIcon className="h-4 w-4 text-indigo-600" />
            Phased send (250 max / phase)
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-gray-600 dark:text-gray-300">
              Contacts per phase
              <input
                type="number"
                min={1}
                max={250}
                value={form.sendPolicy.phaseSize ?? 250}
                onChange={(e) => updatePolicy({ phaseSize: Math.min(250, Number(e.target.value) || 250) })}
                className={`${inputClass} mt-1`}
              />
            </label>
            <label className="text-xs text-gray-600 dark:text-gray-300">
              Complete within (hours)
              <input
                type="number"
                min={1}
                max={168}
                value={form.sendPolicy.sendWindowHours ?? 24}
                onChange={(e) => updatePolicy({ sendWindowHours: Math.max(1, Number(e.target.value) || 24) })}
                className={`${inputClass} mt-1`}
              />
            </label>
          </div>
          <p className="mt-3 rounded-lg bg-indigo-50 px-3 py-2 text-xs text-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-200">
            {audienceCount > 0 ? (
              <>
                <strong>{audienceCount}</strong> contacts → <strong>{preview.phases}</strong> phases of{' '}
                <strong>{preview.phaseSize}</strong>
                {preview.phases > 1 ? (
                  <> · ~{preview.intervalHours.toFixed(1)}h between phases</>
                ) : (
                  <> · sends in one phase</>
                )}
              </>
            ) : (
              'Estimate audience to see phase plan'
            )}
          </p>
        </div>

        <div className="rounded-xl border border-white/80 bg-white/90 p-4 dark:border-gray-700 dark:bg-gray-900/60">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
            <UserGroupIcon className="h-4 w-4 text-indigo-600" />
            Audience rules
          </div>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={form.sendPolicy.enforceWeeklyCap !== false}
                onChange={(e) => updatePolicy({ enforceWeeklyCap: e.target.checked })}
              />
              Max 1 marketing message per contact per{' '}
              <input
                type="number"
                min={1}
                max={14}
                className="w-14 rounded border px-1 py-0.5 text-center dark:border-gray-600 dark:bg-gray-800"
                value={form.sendPolicy.weeklyCapDays ?? 7}
                onChange={(e) => updatePolicy({ weeklyCapDays: Number(e.target.value) || 7 })}
              />{' '}
              days
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={form.sendPolicy.excludeNonReaders !== false}
                onChange={(e) => updatePolicy({ excludeNonReaders: e.target.checked })}
              />
              Exclude contacts with no read in{' '}
              <input
                type="number"
                min={7}
                max={90}
                className="w-14 rounded border px-1 py-0.5 text-center dark:border-gray-600 dark:bg-gray-800"
                value={form.sendPolicy.nonReaderExcludeDays ?? 30}
                onChange={(e) => updatePolicy({ nonReaderExcludeDays: Number(e.target.value) || 30 })}
              />{' '}
              days
            </label>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-white/80 bg-white/90 p-4 dark:border-gray-700 dark:bg-gray-900/60">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
          <ChartBarIcon className="h-4 w-4 text-indigo-600" />
          Retry templates (24h × 7 attempts)
        </div>
        <p className="mb-3 text-xs text-gray-500">
          For Meta-limited or failed sends — pick a different approved template per day (falls back to main
          template if empty). Video / image / document headers are attached automatically from the campaign
          unless you upload a file for that day.
        </p>
        <label className="mb-3 flex items-start gap-2 rounded-lg border border-indigo-100 bg-indigo-50/70 px-3 py-2 text-xs text-indigo-950 dark:border-indigo-900/40 dark:bg-indigo-950/30 dark:text-indigo-100">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={form.applyRetryHeaderToAll !== false}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                applyRetryHeaderToAll: e.target.checked,
                retryHeaderMediaPlan: e.target.checked
                  ? Array.from({ length: 7 }, () => emptyRetryHeaderMediaSlot())
                  : prev.retryHeaderMediaPlan,
              }))
            }
          />
          <span>
            <strong>Apply the same header file to all 7 retarget days</strong>
            <span className="mt-0.5 block text-[11px] text-indigo-800/80 dark:text-indigo-200/80">
              {campaignHasMedia
                ? `Uses the campaign ${String(campaignTemplate?.headerFormat || 'media').toLowerCase()} header on every retry that needs media.`
                : campaignNeedsMedia
                  ? 'Upload a header file on the main campaign template first, or uncheck this to upload per day.'
                  : 'Retry templates that need a media header will inherit the campaign file when you add one.'}
            </span>
          </span>
        </label>
        <div className="space-y-2">
          {Array.from({ length: 7 }, (_, i) => {
            const dayTemplateName = form.retryTemplatePlan[i] || form.templateName;
            const dayTemplate = templates.find((t) => t.name === dayTemplateName) || campaignTemplate;
            const dayNeedsMedia = isMediaHeaderFormat(dayTemplate?.headerFormat);
            const dayMedia = form.retryHeaderMediaPlan?.[i] || emptyRetryHeaderMediaSlot();
            return (
              <div key={i} className="rounded-lg border border-gray-100 p-2 dark:border-gray-800">
                <div className="flex items-center gap-3">
                  <span className="w-14 shrink-0 text-xs font-medium text-gray-600 dark:text-gray-300">
                    Day {i + 1}
                  </span>
                  <WhatsAppTemplateSearchSelect
                    className="min-w-0 flex-1"
                    templates={approvedTemplates}
                    value={form.retryTemplatePlan[i] || ''}
                    onChange={(name) => updateRetryTemplate(i, name)}
                    placeholder="Search template name…"
                    emptyLabel="Same as campaign"
                  />
                  {dayNeedsMedia && (
                    <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                      {String(dayTemplate?.headerFormat || 'media')}
                    </span>
                  )}
                </div>
                {dayNeedsMedia && form.applyRetryHeaderToAll !== false && (
                  <p className="mt-1 pl-[4.25rem] text-[11px] text-gray-500">
                    {campaignHasMedia
                      ? 'Header file: same as campaign'
                      : 'Needs a header file — upload it on the main campaign template, or uncheck “apply same header” above.'}
                  </p>
                )}
                {dayNeedsMedia && form.applyRetryHeaderToAll === false && (
                  <div className="mt-2 pl-0 sm:pl-[4.25rem]">
                    <WhatsAppMediaHeaderUploader
                      inputId={`retry-day-${i}-header`}
                      compact
                      headerFormat={dayTemplate?.headerFormat}
                      headerMediaUrl={dayMedia.headerMediaUrl || null}
                      headerMimeType={dayMedia.headerMimeType || null}
                      headerFileName={dayMedia.headerFileName || null}
                      onChange={(media) => updateRetryHeader(i, media)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhatsAppCampaignPolicySection;
